<script setup lang="ts">
import { URL_IMAGE } from '@/config/constants'
import { languageIdBus } from '@/event/languageIdBus'
import { productBus } from '@/event/product/productBus'
import { relatedGroupBus } from '@/event/product/relatedGroupeBus'
import { showSelectedModalProductHappy } from '@/happyTheme/event/product/showSelectedModalProductHappy'
import { onHover } from '@/happyTheme/utils/onHover'
import { toggleSelectedModalProductHappy } from '@/happyTheme/utils/product/toggleSelectedModalProductHappy'
import { excludeRouteDropdownCategoryArray } from '@/storage/navbar/excludeRoute'
import { getOptionName } from '@/utils/product/getOptionName'
import { getProductDescription } from '@/utils/product/getProductDescription'
import { getProductName } from '@/utils/product/getProductName'
import { hasDescriptionForLanguage } from '@/utils/product/hasDescriptionForLanguage'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import SelectOptionProductHappy from './SelectOptionProductHappy.vue'
import InputQuantityProductHappy from '@/happyTheme/components/product/InputQuantityProductHappy.vue'
import { type SelectedOptionHappy } from '@/happyTheme/interfaces/product/selectedOptionInterface'
import { selectOptionsListHappy } from '@/happyTheme/event/product/selectOptionsListHappy'
import type { Option } from '@/storage/products'
import { openStatesHappy } from '@/happyTheme/event/product/openStatesHappy'
import { getEmptySelectedValuesHappy } from '@/happyTheme/utils/product/modal/getEmptySelectedValuesHappy'
import {
    messageErrorSelectOptionHappy,
    messageErrorSelectOptionHappyEmpty
} from '@/happyTheme/event/product/messageErrorSelectOptionHappy'
import { visibleOptionsHappy } from '@/happyTheme/event/product/visibleOptionHappy'
import MessageErrorOptionHappy from '@/happyTheme/components/product/MessageErrorOptionHappy.vue'
import { selectedPriceProductHappy } from '@/happyTheme/utils/product/modal/selectedPriceProductHappy'
import { addToCartHappy } from '@/happyTheme/utils/product/modal/addToCartHappy'
import { showBannerMessageSpecial } from '@/happyTheme/event/messageSpecial/showBannerMessageSpecial'

const route = useRoute()
const optionRefs = ref<Record<number, HTMLElement>>({})
let observer: MutationObserver | null = null

const heightBottomModal = ref(64)

const {
    isActive: isHovered1,
    onPointerEnter: onEnter1,
    onPointerLeave: onLeave1,
    onClick: onClick1,
    onUnclick: onUnclick1
} = onHover()

const setOptionRef = (el: HTMLElement | null, index: number) => {
    if (el) {
        optionRefs.value[index] = el
    } else {
        delete optionRefs.value[index]
    }
}

const handleScroll = () => {
    const modalBody = document.getElementById('modal-body-product-selected-happy')
    if (!modalBody) return

    const viewportHeight = window.innerHeight
    const viewportCenter = viewportHeight / 2

    visibleOptionsHappy.value = visibleOptionsHappy.value.map(() => false)

    let closestElement = null
    let closestDistance = Infinity

    const openedIndices: number[] = []

    openStatesHappy.value.forEach((isOpen, idx) => {
        if (!isOpen) return

        openedIndices.push(idx)

        const optionElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${idx}"]`
        )
        if (!optionElement) return

        const rect = optionElement.getBoundingClientRect()
        const elementTop = rect.top
        const elementHeight = rect.height
        const elementBottom = rect.bottom
        const elementCenter = elementTop + elementHeight / 2

        const isVisible = elementBottom > 0 && elementTop < viewportHeight
        if (!isVisible) return

        const distanceFromCenter = Math.abs(viewportCenter - elementCenter)

        if (distanceFromCenter < closestDistance) {
            closestDistance = distanceFromCenter
            closestElement = idx
        }
    })

    if (openedIndices.length) {
        const firstIdx = openedIndices[0]
        const lastIdx = openedIndices[openedIndices.length - 1]

        const firstElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${firstIdx}"]`
        )
        const lastElement = document.querySelector(
            `.div-v-for-option-product-happy[data-index="${lastIdx}"]`
        )

        if (firstElement && firstElement.getBoundingClientRect().top > viewportHeight) {
            closestElement = firstIdx
        } else if (lastElement && lastElement.getBoundingClientRect().bottom < 0) {
            closestElement = lastIdx
        }
    }

    if (closestElement !== null) {
        visibleOptionsHappy.value[closestElement] = true
    }
}

function selectedOption(option: SelectedOptionHappy) {
    selectOptionsListHappy.value[option.option_id] = option
}

function getTotalSelectedQuantityRadio(optionId: number): number {
    if (selectOptionsListHappy.value[optionId]) {
        const radioOptionId =
            selectOptionsListHappy.value[optionId].selected_values.radio_option
                .option_value_id

        return radioOptionId ? 1 : 0
    }

    return 0
}

function getTotalSelectedQuantitySelect(optionId: number) {
    let result = {
        freeQuantity: 0,
        paidQuantity: 0,
        freeActiveQuantity: 0,
        paidActiveQuantity: 0
    }

    if (!selectOptionsListHappy.value[optionId]) {
        return result
    }

    const option = productBus.value?.options.find((product) => {
        return product.option_id === optionId
    })

    if (option?.type === 'select' || option?.type === 'checkbox') {
        const countResetFree = selectOptionsListHappy.value[
            optionId
        ].selected_values.checkbox_option.option_value_free.reduce((acc, element) => {
            if (element.option_value_reset !== 1) {
                acc++
            }
            return acc
        }, 0)

        const countResetPaid = selectOptionsListHappy.value[
            optionId
        ].selected_values.checkbox_option.option_value_paid.reduce((acc, element) => {
            if (element.option_value_reset !== 1) {
                acc++
            }
            return acc
        }, 0)

        result.freeActiveQuantity = countResetFree
        result.paidActiveQuantity = countResetPaid
    }

    const resetOption =
        option?.values.filter((o) => o.reset == 1).map((o) => o.option_value_id) || []

    if (
        (option?.free_option_count === 0 && option.max_option_count === 0) ||
        (option?.free_option_count !== 0 && option?.max_option_count === 0)
    ) {
        result.freeQuantity = option.free_option_count
        result.paidQuantity = option.values.length - resetOption?.length
        return result
    }

    if (
        option?.free_option_count !== 0 &&
        option?.max_option_count &&
        option?.max_option_count !== 0 &&
        option?.free_option_count
    ) {
        result.freeQuantity = 0
        result.paidQuantity = option?.max_option_count - option?.free_option_count || 0
        return result
    }

    return result
}

function updateHeightForIdx(idx: number) {
    const el = optionRefs.value[idx]
    if (el) {
        const rh = el.getBoundingClientRect().height
        if (rh && rh > 72) {
            heightBottomModal.value = rh + 72
        }
    }
}

async function toggleShowDropdownOption(index: number, option: Option) {
    if (!selectOptionsListHappy.value[option.option_id]) {
        selectOptionsListHappy.value[option.option_id] =
            getEmptySelectedValuesHappy(option)
    }

    openStatesHappy.value[index] = !openStatesHappy.value[index]

    const isFirstActivateElement = Object.values(openStatesHappy.value)
        .map((isOpen, idx) => ({ idx, isOpen }))
        .filter(({ isOpen }) => isOpen)

    if (isFirstActivateElement.length === 1) {
        openStatesHappy.value.forEach((isOpen, idx) => {
            if (isOpen) {
                visibleOptionsHappy.value[idx] = true
                return
            }
        })
    }
}

const getEnhancedGroupedOptions = (optionId: number) => {
    if (!selectOptionsListHappy.value[optionId]) return []

    const option = productBus.value?.options.find((opt) => opt.option_id === optionId)
    if (!option) return []

    const allOptions = []

    // Radio option
    if (
        option.type === 'radio' &&
        selectOptionsListHappy.value[optionId].selected_values.radio_option
            .option_value_id
    ) {
        allOptions.push({
            id: `radio-${optionId}`,
            name: selectOptionsListHappy.value[optionId].selected_values.radio_option
                .option_value_name,
            type: 'radio',
            count: 1,
            price:
                selectOptionsListHappy.value[optionId].selected_values.radio_option
                    .option_value_price || 0
        })
    }

    // Free options
    if (
        (option.type === 'select' || option.type === 'checkbox') &&
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option
            .option_value_free.length > 0
    ) {
        const freeOptions =
            selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                .option_value_free
        const groupedFree = groupByNameEnhanced(freeOptions, 'free', optionId)
        allOptions.push(...groupedFree)
    }

    // Paid options
    if (
        (option.type === 'select' || option.type === 'checkbox') &&
        selectOptionsListHappy.value[optionId].selected_values.checkbox_option
            .option_value_paid.length > 0
    ) {
        const paidOptions =
            selectOptionsListHappy.value[optionId].selected_values.checkbox_option
                .option_value_paid
        const groupedPaid = groupByNameEnhanced(paidOptions, 'paid', optionId)
        allOptions.push(...groupedPaid)
    }

    return allOptions
}

interface GroupedItem {
    id: string
    name: string
    type: string
    count: number
    price: number
    totalPrice: number
    originalData: any
}

function groupByNameEnhanced(
    options: any[],
    type: string,
    optionId: number
): GroupedItem[] {
    const grouped: Record<string, GroupedItem> = {}

    options.forEach((value) => {
        const name = value.option_value_name
        if (!grouped[name]) {
            grouped[name] = {
                id: `${type}-${optionId}-${name}`,
                name: name,
                type: type,
                count: 0,
                price: Number(value.option_value_price) || 0,
                totalPrice: 0,
                originalData: value
            }
        }

        grouped[name].count++
        grouped[name].totalPrice = grouped[name].price * grouped[name].count
    })

    return Object.values(grouped)
}

watch(
    () => productBus.value?.options,
    (opts) => {
        if (!opts) return
        openStatesHappy.value = new Array(opts.length).fill(false)
        messageErrorSelectOptionHappy.value = new Array(opts.length).fill(
            messageErrorSelectOptionHappyEmpty
        )
    },
    { immediate: true }
)

watch(
    () => showSelectedModalProductHappy.value,
    (isVisible) => {
        if (!isVisible) {
            const html = document.documentElement
            if (html.classList.contains('modal-open')) {
                html.classList.remove('modal-open')
            }
        }
    }
)

watch(
    () => route.name,
    (routeName) => {
        if (routeName) {
            const routeNameString = String(routeName)
            if (!excludeRouteDropdownCategoryArray.includes(routeNameString)) {
                showSelectedModalProductHappy.value = false
            }
        }
    },
    {
        deep: true
    }
)

watch(
    openStatesHappy,
    async () => {
        await nextTick()

        const opened = openStatesHappy.value
            .map((isOpen, idx) => (isOpen ? idx : -1))
            .filter((idx) => idx >= 0)

        if (!opened.length) {
            heightBottomModal.value = 64
            return
        }

        const lastIdx = opened[opened.length - 1]

        updateHeightForIdx(lastIdx)

        if (observer) observer.disconnect()
        observer = new MutationObserver(() => {
            updateHeightForIdx(lastIdx)
        })
        observer.observe(document.body, { childList: true, subtree: true })
    },
    { deep: true, immediate: true }
)

onMounted(() => {
    openStatesHappy.value = openStatesHappy.value.map(() => false)
    handleScroll()

    const modalBody = document.getElementById('modal-body-product-selected-happy')
    if (!modalBody) return

    modalBody.addEventListener('scroll', handleScroll)
})

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll)
})
</script>

<template>
    <section
        class="section-modal-product-selected-happy"
        v-if="productBus && openStatesHappy.length === productBus.options.length"
    >
        <section
            @click.stop="toggleSelectedModalProductHappy"
            v-if="showSelectedModalProductHappy && productBus"
            class="overlay-section-product-happy"
        ></section>

        <section
            class="section-selected-product-happy"
            :class="{
                'pt-20': !showBannerMessageSpecial,
                'pt-[136px]': showBannerMessageSpecial
            }"
        >
            <div class="div-button-close-product-selected-happy">
                <button
                    @mouseenter="onEnter1"
                    @mouseleave="onLeave1"
                    @pointerdown="onClick1"
                    @pointerup="onUnclick1"
                    class="close-modal-product-selected-happy"
                    @click="toggleSelectedModalProductHappy"
                >
                    <svg
                        class="svg-cross-product-selected-happy"
                        :class="{
                            'stroke-border-red-happy-light': isHovered1,
                            'happy-light:stroke-text-dark-happy-light happy-dark:stroke-bg-happy-light':
                                !isHovered1
                        }"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                    >
                        <path
                            d="M16 8L8 16M8.00001 8L16 16"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        />
                    </svg>
                </button>
            </div>

            <section
                class="modal-body-product-selected-happy"
                id="modal-body-product-selected-happy"
            >
                <section class="section-product-selected-happy">
                    <div class="div-product-selected-happy">
                        <img
                            loading="lazy"
                            class="img-product-selected-happy"
                            :src="`${URL_IMAGE}${productBus.image}`"
                            alt=""
                        />
                    </div>

                    <div class="div-product-information-selected-happy">
                        <h3 class="h3-product-selected-happy">
                            {{ getProductName(productBus) }}
                        </h3>

                        <p
                            v-if="
                                hasDescriptionForLanguage(
                                    productBus.description,
                                    languageIdBus
                                )
                            "
                        >
                            {{ getProductDescription(productBus) }}
                        </p>
                    </div>
                </section>

                <section
                    class="section-product-option-selected-happy"
                    v-if="productBus.options.length > 0"
                >
                    <div class="div-product-option-information-happy">
                        <h4 class="h4-product-option-selected-happy">Options</h4>

                        <p class="text-obligation-option-selected-happy">
                            <span class="text-sm">*</span>Obligatoire
                        </p>
                    </div>
                    <section class="div-form-product-happy">
                        <div
                            v-for="(option, idx) in productBus.options"
                            :key="option.option_id"
                            :id="String(option.option_id)"
                            class="w-full"
                            :class="{
                                hidden: relatedGroupBus.includes(option.option_id)
                            }"
                        >
                            <div
                                class="div-v-for-option-product-happy"
                                :data-index="idx"
                                :class="{
                                    shake:
                                        messageErrorSelectOptionHappy[idx].type ===
                                        'error',
                                    'border-red-eronor':
                                        messageErrorSelectOptionHappy[idx].type ===
                                        'error',

                                    'happy-light:border-bg-happy-light happy-dark:border-text-dark-happy-light':
                                        messageErrorSelectOptionHappy[idx].type !==
                                        'error'
                                }"
                            >
                                <div
                                    v-if="!relatedGroupBus.includes(option.option_id)"
                                    :class="{
                                        'border-b border-border-gray-happy-light':
                                            openStatesHappy[idx]
                                    }"
                                    @click.stop="toggleShowDropdownOption(idx, option)"
                                >
                                    <div class="form-option-title-product-happy">
                                        <h4
                                            :id="getOptionName(option, languageIdBus)"
                                            v-if="
                                                option.required &&
                                                option.type === 'select'
                                            "
                                            class="h4-option-happy"
                                        >
                                            <p class="">
                                                {{ getOptionName(option, languageIdBus) }}
                                                <span
                                                    v-if="option.required"
                                                    class="choice"
                                                >
                                                    *</span
                                                >
                                            </p>
                                        </h4>

                                        <h4
                                            v-else-if="
                                                option.type === 'radio' &&
                                                !option.required
                                            "
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus) }}
                                        </h4>

                                        <h4
                                            v-else-if="
                                                option.type === 'radio' && option.required
                                            "
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus) }}
                                            <span class="choice">*</span>
                                        </h4>

                                        <h4
                                            v-else
                                            :id="getOptionName(option, languageIdBus)"
                                            class="h4-option-happy"
                                        >
                                            {{ getOptionName(option, languageIdBus) }}
                                        </h4>

                                        <svg
                                            class="svg-arrow-happy-option-product-happy"
                                            :class="{
                                                'rotate-180': openStatesHappy[idx],
                                                'pb-0.5': !openStatesHappy[idx]
                                            }"
                                            viewBox="0 0 16 10"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                fill-rule="evenodd"
                                                clip-rule="evenodd"
                                                d="M15.6994 0.852272C15.2985 0.45142 14.6486 0.45142 14.2478 0.852272L8.13076 6.96921L2.01369 0.852272C1.61284 0.45142 0.963042 0.45142 0.56219 0.852272C0.161338 1.25312 0.161338 1.90292 0.56219 2.30377L7.40501 9.14663C7.59743 9.33912 7.85855 9.44727 8.13076 9.44727C8.40297 9.44727 8.66409 9.33912 8.85651 9.14663L15.6994 2.30377C16.1002 1.90292 16.1002 1.25312 15.6994 0.852272Z"
                                            />
                                        </svg>
                                    </div>

                                    <div
                                        v-if="selectOptionsListHappy[option.option_id]"
                                        class="option-selected-happy border-t"
                                    >
                                        <TransitionGroup
                                            name="option-cascade"
                                            tag="div"
                                            enter-active-class="transition-all duration-400 ease-out"
                                            enter-from-class="opacity-0 transform translate-x-4 scale-90"
                                            enter-to-class="opacity-100 transform translate-x-0 scale-100"
                                            leave-active-class="transition-all duration-300 ease-in"
                                            leave-from-class="opacity-100 transform translate-x-0 scale-100"
                                            leave-to-class="opacity-0 transform -translate-x-4 scale-90"
                                            move-class="transition-transform duration-300"
                                        >
                                            <div
                                                v-for="groupedOption in getEnhancedGroupedOptions(
                                                    option.option_id
                                                )"
                                                :key="groupedOption.id"
                                                class="grouped-option-item"
                                                :class="{
                                                    'option-paid':
                                                        groupedOption.type === 'paid',
                                                    'option-free':
                                                        groupedOption.type === 'free',
                                                    'option-radio':
                                                        groupedOption.type === 'radio'
                                                }"
                                            >
                                                <span class="option-name">
                                                    - {{ groupedOption.name }}
                                                </span>

                                                <!-- ✅ Badge pour la quantité -->
                                                <span
                                                    v-if="groupedOption.count > 1"
                                                    class="quantity-badge"
                                                >
                                                    x{{ groupedOption.count }}
                                                </span>

                                                <!-- ✅ Prix total si payant -->
                                                <span
                                                    v-if="
                                                        groupedOption.type === 'paid' &&
                                                        'totalPrice' in groupedOption &&
                                                        groupedOption.totalPrice > 0
                                                    "
                                                    class="price-info-happy"
                                                >
                                                    (+{{
                                                        groupedOption.totalPrice.toFixed(
                                                            2
                                                        )
                                                    }}€)
                                                </span>
                                            </div>
                                        </TransitionGroup>
                                    </div>

                                    <MessageErrorOptionHappy
                                        v-if="
                                            messageErrorSelectOptionHappy[idx].message &&
                                            messageErrorSelectOptionHappy[idx].type
                                        "
                                        :message="
                                            messageErrorSelectOptionHappy[idx].message
                                        "
                                        :type="messageErrorSelectOptionHappy[idx].type"
                                    />
                                </div>

                                <SelectOptionProductHappy
                                    v-if="openStatesHappy[idx]"
                                    :options="option"
                                    :name_option="getOptionName(option, languageIdBus)"
                                    @select-option="
                                        (opt: SelectedOptionHappy) => selectedOption(opt)
                                    "
                                />
                            </div>

                            <div
                                v-if="openStatesHappy[idx]"
                                :ref="(el) => setOptionRef(el as HTMLElement, idx)"
                                class="div-option-selected-happy"
                                :data-index="idx"
                                :class="{
                                    'fixed-bottom':
                                        openStatesHappy[idx] && visibleOptionsHappy[idx],
                                    'relative hidden':
                                        openStatesHappy[idx] && !visibleOptionsHappy[idx]
                                }"
                            >
                                <div>
                                    <p
                                        class="font-semibold"
                                        v-if="visibleOptionsHappy[idx]"
                                    >
                                        {{ getOptionName(option, languageIdBus) }}
                                    </p>
                                    <span
                                        v-if="
                                            option.values.find(
                                                (e) => e.price_status === 'special'
                                            )
                                        "
                                        class="text-xs italic font-bold text-border-red-happy-light"
                                        >certaines options sont payantes
                                    </span>
                                    <span
                                        class="span-choice-happy-required"
                                        v-if="option.type === 'radio' && option.required"
                                    >
                                        <p>
                                            {{
                                                getTotalSelectedQuantityRadio(
                                                    option.option_id
                                                )
                                            }}/1 gratuit
                                        </p>
                                    </span>
                                    <span
                                        class="span-choice-happy"
                                        v-if="option.type === 'radio' && !option.required"
                                    >
                                        <p>
                                            {{
                                                getTotalSelectedQuantityRadio(
                                                    option.option_id
                                                )
                                            }}/1 gratuit
                                        </p>
                                    </span>
                                    <span
                                        v-if="option.type === 'select' && option.required"
                                    >
                                        <div
                                            class="span-choice-happy-required"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p>
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity -
                                                    option.free_option_count
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy">
                                                {{
                                                    Math.max(
                                                        getTotalSelectedQuantitySelect(
                                                            option.option_id
                                                        ).paidActiveQuantity
                                                    )
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                    </span>
                                    <span
                                        class="span-choice-happy"
                                        v-if="
                                            option.type === 'select' && !option.required
                                        "
                                    >
                                        <div
                                            class="span-choice-happy-required"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p>
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count === 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity -
                                                    option.free_option_count
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selecti-option-happy"
                                            v-if="
                                                option.free_option_count > 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy-required">
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).freeActiveQuantity
                                                }}/{{ option.free_option_count }}
                                                gratuit
                                            </p>
                                            <p
                                                class="span-choice-happy"
                                                v-if="
                                                    option.max_option_count -
                                                        option.free_option_count !==
                                                    0
                                                "
                                            >
                                                {{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidActiveQuantity
                                                }}/{{
                                                    getTotalSelectedQuantitySelect(
                                                        option.option_id
                                                    ).paidQuantity
                                                }}
                                                payant
                                            </p>
                                        </div>
                                        <div
                                            class="div-number-selection-option-happy"
                                            v-if="
                                                option.free_option_count === 0 &&
                                                option.max_option_count > 0
                                            "
                                        >
                                            <p class="span-choice-happy">
                                                {{
                                                    Math.max(
                                                        getTotalSelectedQuantitySelect(
                                                            option.option_id
                                                        ).paidActiveQuantity
                                                    )
                                                }}/{{
                                                    option.max_option_count -
                                                    option.free_option_count
                                                }}
                                                maximum
                                            </p>
                                        </div>
                                    </span>
                                </div>
                                <!-- <ButtonValidateOptionValueHappy
                                    @click.stop="
                                        async () =>
                                            await validateOptionValueHappy(idx, option)
                                    "
                                /> -->
                            </div>
                        </div>
                    </section>
                    <div :style="{ minHeight: `${heightBottomModal}px` }"></div>
                </section>
                <div class="modal-footer-product-happy">
                    <InputQuantityProductHappy />
                    <button class="button-add-to-cart-happy" @click="addToCartHappy()">
                        Ajouter {{ selectedPriceProductHappy(productBus.price) }} €
                    </button>
                </div>
            </section>
        </section>
    </section>
</template>

<style scoped>
.section-modal-product-selected-happy {
    @apply z-20 fixed top-0 w-screen  overflow-y-auto transition-transform duration-300  flex justify-end;
    height: 100%;
}

.overlay-section-product-happy {
    @apply fixed top-0 w-screen h-full transition-opacity duration-300 opacity-70;
    @apply happy-light:bg-text-dark-happy-light;
    @apply happy-dark:bg-bg-happy-light;
}

.section-selected-product-happy {
    @apply relative w-full min-h-screen  bg-border-gray-happy-light;
    @apply flex flex-col z-10;
}

.div-button-close-product-selected-happy {
    @apply fixed flex p-1 shrink-0  mt-2 ml-2 rounded-xl shadow-sm shadow-bg-happy-light happy-light:bg-bg-happy-light happy-dark:bg-text-dark-happy-light;
}

.div-button-close-product-selected-happy:active {
    @apply bg-banner-product-happy;
}

.svg-cross-product-selected-happy {
    @apply w-10 stroke-bg-happy-light;
}

.div-button-close-product-selected-happy:active .svg-cross-product-selected-happy {
    @apply stroke-text-dark-happy-light;
}

.modal-body-product-selected-happy {
    @apply flex flex-col overflow-y-auto;
}

.section-product-selected-happy {
    @apply max-w-full flex flex-col p-2 happy-light:bg-bg-happy-light happy-dark:bg-text-dark-happy-light;
    min-height: fit-content;
}

.div-product-selected-happy {
    @apply w-full flex flex-col items-center gap-2 mb-2 happy-light:bg-bg-happy-light happy-dark:bg-text-dark-happy-light;
}

.img-product-selected-happy {
    @apply max-w-80;
}

.div-product-information-selected-happy {
    @apply w-full flex flex-col gap-2;
}

.h3-product-selected-happy {
    @apply font-bold text-3xl;
}

.div-product-option-selected-happy {
    @apply w-full;
}
.section-product-option-selected-happy {
    @apply w-full h-full flex flex-col px-2 pt-2 bg-border-gray-happy-light;
}

.div-product-option-information-happy {
    @apply w-full flex justify-between items-center;
}

.h4-product-option-selected-happy {
    @apply font-semibold text-lg pb-2 happy-dark:text-text-dark-happy-light;
}

.text-obligation-option-selected-happy {
    @apply text-xs text-border-red-happy-light font-semibold;
}

.div-form-product-happy {
    @apply flex flex-col gap-3;
}

.div-v-for-option-product-happy {
    @apply rounded-xl border-2 happy-light:bg-bg-happy-light  happy-dark:bg-text-dark-happy-light w-full;
}

.form-option-title-product-happy {
    @apply flex flex-row items-center justify-between px-3 py-2;
}

.h4-option-happy {
    @apply flex font-semibold whitespace-pre-wrap;
}
.choice {
    @apply text-border-red-happy-light;
}

.svg-arrow-happy-option-product-happy {
    @apply min-w-5 min-h-5 max-w-5 max-h-5 happy-light:fill-text-dark-happy-light happy-dark:fill-bg-happy-light transition-transform duration-300;
}

.option-selected-happy {
    @apply px-3 py-1 text-sm;
}

.price-info-happy {
    @apply text-border-red-happy-light;
}
/* .div-option-selected-happy {
    @apply hidden;
} */

.fixed-bottom {
    @apply fixed rounded-none bottom-[55px] border-b happy-light:border-bg-happy-light happy-dark:border-text-dark-happy-light  w-full z-40 py-4 px-3 right-0;
    @apply flex items-end happy-dark:bg-bg-happy-light happy-light:bg-text-dark-happy-light justify-between;
    @apply happy-light:text-bg-happy-light happy-dark:text-text-dark-happy-light;
}

.span-choice-happy-required {
    @apply text-border-red-happy-light font-semibold;
}
.span-choice-happy {
    @apply text-button-yellow-happy-light  font-semibold;
}
.div-number-selecti-option-happy {
    @apply flex gap-2;
}

.modal-footer-product-happy {
    @apply fixed bottom-0 w-full flex justify-between items-center gap-2 py-2 px-3 happy-dark:bg-bg-happy-light happy-light:bg-text-dark-happy-light z-30;
}

.button-add-to-cart-happy {
    @apply min-w-36 py-2 px-2 rounded-lg  happy-dark:text-text-dark-happy-light font-semibold bg-button-yellow-happy-light;
}

@media (hover: hover) {
}

@media screen and (min-width: 416px) {
    .section-selected-product-happy {
        @apply max-w-[26rem];
    }
    .div-option-selected-happy {
        @apply max-w-[26rem];
    }
    .modal-footer-product-happy {
        @apply max-w-[26rem];
    }
}

@keyframes shake {
    0% {
        transform: rotate(0deg);
    }
    15% {
        transform: rotate(5deg);
    }
    30% {
        transform: rotate(0eg);
    }
    45% {
        transform: rotate(-5deg);
    }
    60% {
        transform: rotate(0deg);
    }
    75% {
        transform: rotate(-5deg);
    }
    100% {
        transform: rotate(0deg);
    }
}

.shake {
    animation: shake 0.5s ease-in-out;
}
</style>
